import { useStateWithGetter } from "@/hooks/use-state-with-getter";
import { HistoryLocation, useRouter } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";

const DEBUG = false;

function useRuntimeHistoryState() {
  const router = useRouter();

  const [entries, getEntries, setEntries] = useStateWithGetter<
    readonly (HistoryLocation | null)[]
  >([router.history.location]);
  const [index, getIndex, setIndex] = useStateWithGetter(0);

  const getEntryAt = useCallback(
    (index: number) => {
      const entries = getEntries();
      return index >= 0 && index < entries.length ? entries[index] : null;
    },
    [getEntries],
  );

  const go = useCallback(
    (delta: number, location: HistoryLocation) => {
      const entries = getEntries();
      const index = getIndex();

      let newIndex = index + delta;
      const minIndex = 0;
      let maxIndex = entries.length - 1;

      const newEntries = [...entries];

      if (newIndex < minIndex) {
        const gapSize = minIndex - newIndex;
        newEntries.unshift(...Array.from({ length: gapSize }, () => null));
        newIndex += gapSize;
        maxIndex += gapSize;
      } else if (newIndex > maxIndex) {
        const gapSize = newIndex - maxIndex;
        newEntries.push(...Array.from({ length: gapSize }, () => null));
        maxIndex += gapSize;
      }

      newEntries[newIndex] = location;

      setEntries(newEntries);
      setIndex(newIndex);
    },
    [getEntries, getIndex, setEntries, setIndex],
  );

  const push = useCallback(
    (location: HistoryLocation) => {
      const entries = getEntries();
      const index = getIndex();
      const newEntries = [...entries.slice(0, index + 1), location];
      setEntries(newEntries);
      setIndex(newEntries.length - 1);
    },
    [getEntries, getIndex, setEntries, setIndex],
  );

  const replace = useCallback(
    (location: HistoryLocation) => {
      const entries = getEntries();
      const index = getIndex();
      const newEntries = [...entries];
      newEntries[index] = location;
      setEntries(newEntries);
    },
    [getEntries, getIndex, setEntries],
  );

  return useMemo(
    () => ({
      entries,
      getEntries,

      index,
      getIndex,

      previousEntry: getEntryAt(index - 1),
      nextEntry: getEntryAt(index + 1),

      go,
      push,
      replace,
    }),
    [entries, getEntries, index, getIndex, getEntryAt, go, push, replace],
  );
}

export function useRouterHistoryService() {
  const router = useRouter();

  const runtimeHistory = useRuntimeHistoryState();

  useEffect(() => {
    const cleanupFns: (() => void)[] = [];

    cleanupFns.push(
      router.history.subscribe(({ action, location }) => {
        if (DEBUG)
          console.debug("Router history action", action.type, action, location);

        switch (action.type) {
          case "GO":
          case "BACK":
          case "FORWARD": {
            const delta =
              action.type === "GO"
                ? action.index
                : action.type === "BACK"
                  ? -1
                  : 1;
            runtimeHistory.go(delta, location);
            break;
          }

          case "PUSH": {
            runtimeHistory.push(location);
            break;
          }

          case "REPLACE": {
            runtimeHistory.replace(location);
            break;
          }
        }
      }),
    );

    return () => {
      for (const fn of cleanupFns) {
        fn();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  useEffect(() => {
    if (DEBUG) console.debug("RuntimeHistoryState changed", runtimeHistory);
  }, [runtimeHistory]);

  return useMemo(
    () => ({
      entries: runtimeHistory.entries,
      index: runtimeHistory.index,
      previousEntry: runtimeHistory.previousEntry,
      nextEntry: runtimeHistory.nextEntry,
    }),
    [runtimeHistory],
  );
}

export type RuntimeHistoryState = ReturnType<typeof useRouterHistoryService>;

export const RuntimeHistoryContext = createContext<RuntimeHistoryState | null>(
  null,
);

export function useRuntimeHistory() {
  const context = useContext(RuntimeHistoryContext);
  if (!context) {
    throw new Error(
      "useRuntimeHistory must be used within a RuntimeHistoryContext provider",
    );
  }
  return context;
}
